export const RETRYABLE_METHODS = ['get', 'head', 'options'];

// min(cap, base × 2^n) plus up to 30% jitter, for the nth retry (from 0).
const makeBackoff =
  (baseDelay: number, maxDelay: number) => (retryCount: number) => {
    const exponentialDelay = Math.min(
      maxDelay,
      baseDelay * Math.pow(2, retryCount)
    );
    const jitter = Math.random() * 0.3 * exponentialDelay; // 30% jitter
    return exponentialDelay + jitter;
  };

// 502/503 backoff: 1s doubling up to a 5s cap, plus up to 30% jitter.
export const getBackoffTime = makeBackoff(1000, 5000);

// 429: longer gaps than 5xx, capped so raising the retry count can't make the
// waits explode. Placeholder values (MC-3932), not final. The Retry-After
// header is not read yet (MC-3979).
export const RATE_LIMIT_MAX_RETRIES = 3;
export const RATE_LIMIT_BASE_DELAY = 5000; // 5 seconds
export const RATE_LIMIT_MAX_DELAY = 30000; // 30 seconds, before jitter
export const getRateLimitBackoffTime = makeBackoff(
  RATE_LIMIT_BASE_DELAY,
  RATE_LIMIT_MAX_DELAY
);

// 504: single retry after a random 5–10s wait.
export const getGatewayTimeoutDelay = () => 5000 + Math.random() * 5000;

type RetryRule = {
  maxRetries: number;
  getDelay: (retryCount: number) => number;
};

// The parts of an axios error the retry rules read.
export type RetryableError =
  | {
      response?: { status?: number };
      config?: { method?: string; url?: string; _retryCount?: number };
    }
  | null
  | undefined;

// Retry budget: `config._retryCount` is one counter per request, shared by
// every status, and each status compares it with its own `maxRetries`. This is
// deliberate: a request that keeps failing with different statuses stops
// retrying early instead of collecting a fresh budget for each status, which
// keeps total load capped during an incident. For example, GET → 503 → 504 is
// not retried after the 504, because one retry has already been spent.
export const RETRY_POLICY: Record<number, RetryRule> = {
  502: { maxRetries: 5, getDelay: getBackoffTime },
  503: { maxRetries: 5, getDelay: getBackoffTime },
  429: {
    maxRetries: RATE_LIMIT_MAX_RETRIES,
    getDelay: getRateLimitBackoffTime,
  },
  504: { maxRetries: 1, getDelay: getGatewayTimeoutDelay },
};

// Returns the rule for a retryable request (by status and method), or null.
const getRetryRule = (error: RetryableError) => {
  const status = error?.response?.status;
  const rule = status === undefined ? undefined : RETRY_POLICY[status];
  const method = (error?.config?.method || 'get').toLowerCase();

  return rule && RETRYABLE_METHODS.includes(method) ? rule : null;
};

const getRetryCount = (error: RetryableError) =>
  error?.config?._retryCount || 0;

// "503 GET /api/v1/...", used to prefix the log lines below.
const describeRequest = (error: RetryableError) => {
  const method = (error?.config?.method || 'get').toUpperCase();
  return `${error?.response?.status} ${method} ${error?.config?.url ?? ''}`;
};

// Returns ms to wait before the next retry, or null for "do not retry".
export const getRetryDelay = (error: RetryableError): number | null => {
  const rule = getRetryRule(error);
  const retryCount = getRetryCount(error);

  if (!rule || retryCount >= rule.maxRetries) {
    return null;
  }

  return rule.getDelay(retryCount);
};

// Logs each retry, and the point where a retryable request gives up, so a
// screen that is retrying can be told apart from one that is just slow.
export const logRetryDecision = (
  error: RetryableError,
  retryDelay: number | null
) => {
  const rule = getRetryRule(error);

  if (!rule) {
    return;
  }

  const retryCount = getRetryCount(error);
  const request = describeRequest(error);

  if (retryDelay === null) {
    console.warn(
      `[retry] ${request}: giving up after ${retryCount} ${retryCount === 1 ? 'retry' : 'retries'}`
    );
  } else {
    console.warn(
      `[retry] ${request}: retry ${retryCount + 1} of ${rule.maxRetries} in ${Math.round(retryDelay)}ms`
    );
  }
};

// Logs when an expired token is refreshed and the request is replayed once.
export const logTokenRefresh = (error: RetryableError) => {
  console.warn(
    `[retry] ${describeRequest(error)}: token expired, refreshing it and replaying once`
  );
};
